import { useCallback } from 'react'
import { supabase } from './supabase'
import { getTierByCi, CI_ACTIONS } from './press'

// ── insertRankUpNotification ──────────────────────────────────
// Inserts a notification when a user ranks up
async function insertRankUpNotification(userId, newTier) {
  await supabase.from('notifications').insert({
    user_id: userId,
    actor_id: userId,
    type: 'rank_up',
    message: `you've ranked up to ${newTier.title}. keep going.`,
  })
}

// ── useColumnInches ───────────────────────────────────────────
// Call awardCI(userId, action, refId) to give CI to a writer
// Returns { newTotal, rankedUp, newTier } so caller can trigger banner

export function useColumnInches() {

  const awardCI = useCallback(async (userId, action, refId = null) => {
    const ci = CI_ACTIONS[action]
    if (!ci || !userId) return null

    // Fetch current CI
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('column_inches, is_voice')
      .eq('id', userId)
      .single()

    if (error || !profile) return null

    const oldCI = profile.column_inches || 0
    const newCI = oldCI + ci
    const oldTier = getTierByCi(oldCI, profile.is_voice)
    const newTier = getTierByCi(newCI, profile.is_voice)
    const rankedUp = newTier.rank > oldTier.rank

    // Update profile CI
    await supabase
      .from('profiles')
      .update({ column_inches: newCI })
      .eq('id', userId)

    // Log the event
    await supabase.from('ci_log').insert({
      user_id: userId,
      action,
      ci,
      ref_id: refId ? String(refId) : null,
    })

    // Send rank-up notification if tier changed
    if (rankedUp) {
      await insertRankUpNotification(userId, newTier)
    }

    return { newTotal: newCI, rankedUp, newTier, oldTier }
  }, [])

  return { awardCI }
}

// ── awardPostCI ───────────────────────────────────────────────
// Called when a post is published — awards CI based on length + section
export async function awardPostCI(userId, post) {
  const words = post.body?.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length || 0
  let action = 'post_culture_short'
  if (post.section === 'journal') {
    action = 'post_journal'
  } else if (words >= 1200) {
    action = 'post_culture_long'
  } else if (words >= 600) {
    action = 'post_culture_mid'
  }

  const ciMap = {
    post_culture_short: 8,
    post_culture_mid: 15,
    post_culture_long: 22,
    post_journal: 35,
  }

  const ci = ciMap[action]
  if (!ci) return null

  const { data: profile } = await supabase
    .from('profiles')
    .select('column_inches, is_voice')
    .eq('id', userId)
    .single()

  if (!profile) return null

  const oldCI = profile.column_inches || 0
  const newCI = oldCI + ci
  const oldTier = getTierByCi(oldCI, profile.is_voice)
  const newTier = getTierByCi(newCI, profile.is_voice)

  await supabase.from('profiles').update({ column_inches: newCI }).eq('id', userId)
  await supabase.from('ci_log').insert({
    user_id: userId, action, ci, ref_id: String(post.id),
  })

  // Send rank-up notification if tier changed
  if (newTier.rank > oldTier.rank) {
    await insertRankUpNotification(userId, newTier)
  }

  return {
    newTotal: newCI,
    rankedUp: newTier.rank > oldTier.rank,
    newTier,
    oldTier,
    ciAwarded: ci,
  }
}

// ── awardCommentCI ───────────────────────────────────────────
// Called when a comment lands on a post — awards CI to the post author
export async function awardCommentCI(authorId, postId) {
  const ci = 3  // CI_ACTIONS.comment_received
  if (!authorId) return null

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('column_inches, is_voice')
    .eq('id', authorId)
    .single()

  if (error || !profile) return null

  const newCI = (profile.column_inches || 0) + ci

  await supabase.from('profiles').update({ column_inches: newCI }).eq('id', authorId)
  await supabase.from('ci_log').insert({
    user_id: authorId, action: 'comment_received', ci, ref_id: String(postId),
  })

  return { newTotal: newCI }
}

// ── awardReactionCI ───────────────────────────────────────────
// Called when a reaction is added to a post — awards CI to the post author
// CI aging: reactions on older posts earn more (rewards timeless writing)
export async function awardReactionCI(authorId, reactionType, postId) {
  const actionMap = {
    brought_here: 'reaction_brought_here',
    shifts_something: 'reaction_shifts_something',
    sits_with_me: 'reaction_sits_with_me',
  }
  const action = actionMap[reactionType] || 'reaction_other'
  const baseCi = CI_ACTIONS[action]
  if (!baseCi) return null

  // Fetch post age for multiplier
  let multiplier = 1.0
  try {
    const { data: postData } = await supabase
      .from('posts').select('created_at').eq('id', postId).single()
    if (postData?.created_at) {
      const agedays = (Date.now() - new Date(postData.created_at)) / 86400000
      if (agedays >= 90) multiplier = 2.0
      else if (agedays >= 30) multiplier = 1.5
      else if (agedays >= 7) multiplier = 1.2
    }
  } catch {}

  const ci = Math.round(baseCi * multiplier)

  const { data: profile } = await supabase
    .from('profiles')
    .select('column_inches, is_voice')
    .eq('id', authorId)
    .single()

  if (!profile) return null

  const oldCI = profile.column_inches || 0
  const newCI = oldCI + ci
  const oldTier = getTierByCi(oldCI, profile.is_voice)
  const newTier = getTierByCi(newCI, profile.is_voice)

  await supabase.from('profiles').update({ column_inches: newCI }).eq('id', authorId)
  await supabase.from('ci_log').insert({
    user_id: authorId, action, ci, ref_id: String(postId), multiplier,
  })

  // Send rank-up notification if tier changed
  if (newTier.rank > oldTier.rank) {
    await insertRankUpNotification(authorId, newTier)
  }

  return {
    newTotal: newCI,
    rankedUp: newTier.rank > oldTier.rank,
    newTier,
    oldTier,
  }
}

// ── revertPostCI ──────────────────────────────────────────────
// Called when a published post is deleted — reverses the CI that was awarded
export async function revertPostCI(authorId, post) {
  const words = post.body?.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length || 0
  let ci = 8
  if (post.section === 'journal') ci = 35
  else if (words >= 1200) ci = 22
  else if (words >= 600) ci = 15

  const { data: profile, error } = await supabase
    .from('profiles').select('column_inches').eq('id', authorId).single()
  if (error || !profile) return null

  const newCI = Math.max(0, (profile.column_inches || 0) - ci)
  await supabase.from('profiles').update({ column_inches: newCI }).eq('id', authorId)
  await supabase.from('ci_log').insert({
    user_id: authorId, action: 'post_deleted_revert', ci: -ci, ref_id: String(post.id || ''),
  })
  return { newTotal: newCI }
}
